/**
 * Pure function: build a flat list of segments from a PlannedWorkout structure.
 * Each segment has { start, end, zone, label, type }.
 * Used for display, TCX export, and FIT export.
 * @param {Object} workout - PlannedWorkout (structure, targetDuration, targetIntensity)
 * @returns {Array<{ start: number, end: number, zone: number, label: string, type: string }>}
 */
export function getWorkoutSegments(workout) {
  const profile = [];
  const structure = workout?.structure || { type: 'simple' };
  let currentTime = 0;

  if (structure.type === 'intervals' && structure.intervals) {
    const { warmup, sets, cooldown } = structure.intervals;

    if (warmup) {
      profile.push({
        start: currentTime,
        end: currentTime + warmup.duration,
        zone: warmup.intensity,
        type: 'warmup',
        label: 'Warmup',
      });
      currentTime += warmup.duration;
    }

    sets.forEach((set, setIndex) => {
      for (let rep = 0; rep < set.repetitions; rep++) {
        profile.push({
          start: currentTime,
          end: currentTime + set.workDuration,
          zone: set.workIntensity,
          type: 'work',
          label: `Interval ${rep + 1}`,
        });
        currentTime += set.workDuration;

        if (rep < set.repetitions - 1) {
          profile.push({
            start: currentTime,
            end: currentTime + set.restDuration,
            zone: set.restIntensity,
            type: 'rest',
            label: 'Rest',
          });
          currentTime += set.restDuration;
        }
      }

      if (set.sets && setIndex < sets.length - 1 && set.setRestDuration) {
        profile.push({
          start: currentTime,
          end: currentTime + set.setRestDuration,
          zone: set.restIntensity,
          type: 'rest',
          label: 'Set Rest',
        });
        currentTime += set.setRestDuration;
      }
    });

    if (cooldown) {
      profile.push({
        start: currentTime,
        end: currentTime + cooldown.duration,
        zone: cooldown.intensity,
        type: 'cooldown',
        label: 'Cooldown',
      });
      currentTime += cooldown.duration;
    }
  } else if (structure.type === 'pyramid' && structure.pyramid) {
    const { warmup, segments, cooldown } = structure.pyramid;

    if (warmup) {
      profile.push({
        start: currentTime,
        end: currentTime + warmup.duration,
        zone: warmup.intensity,
        type: 'warmup',
        label: 'Warmup',
      });
      currentTime += warmup.duration;
    }

    segments.forEach((segment, index) => {
      profile.push({
        start: currentTime,
        end: currentTime + segment.duration,
        zone: segment.intensity,
        type: 'segment',
        label: `Segment ${index + 1}`,
      });
      currentTime += segment.duration;
    });

    if (cooldown) {
      profile.push({
        start: currentTime,
        end: currentTime + cooldown.duration,
        zone: cooldown.intensity,
        type: 'cooldown',
        label: 'Cooldown',
      });
      currentTime += cooldown.duration;
    }
  } else if (structure.type === 'custom' && structure.custom) {
    structure.custom.segments.forEach((segment, index) => {
      profile.push({
        start: currentTime,
        end: currentTime + segment.duration,
        zone: segment.intensity,
        type: 'segment',
        label: segment.name || `Segment ${index + 1}`,
      });
      currentTime += segment.duration;
    });
  } else {
    const simple =
      structure.simple ||
      {
        duration: workout?.targetDuration || 3600,
        intensity: workout?.targetIntensity?.zone || 2,
      };
    profile.push({
      start: 0,
      end: simple.duration,
      zone: simple.intensity,
      type: 'main',
      label: 'Main Effort',
    });
  }

  return profile;
}
